import axios, { type AxiosError, type AxiosInstance } from 'axios'
import { API_BASE_URL, API_V2_BASE_URL, APP_ENV } from '@/shared/constants'
import { useAuthStore } from '@/store/useAuthStore'

export const restClient = axios.create({
  baseURL: API_BASE_URL,
  timeout: 20000,
  // NB: do NOT force a default 'Content-Type: application/json' here.
  // Axios auto-sets application/json for plain-object bodies and
  // multipart/form-data (with boundary) for FormData bodies. Forcing JSON
  // makes axios serialise FormData to JSON, turning numeric fields like
  // `gear` into quoted strings and breaking the Go backend's int parsing.
  headers: { Accept: 'application/json' },
})

// v2 APIs are served by Fiber on a different port (:8081). Same auth + error
// handling as restClient — only the base URL differs.
export const restClientV2 = axios.create({
  baseURL: API_V2_BASE_URL,
  timeout: 20000,
  headers: { Accept: 'application/json' },
})

// Shared request interceptor: attach the Bearer token (same 'token' key).
function attachAuth(client: AxiosInstance) {
  client.interceptors.request.use((config) => {
    const token = localStorage.getItem('token') ?? useAuthStore.getState().token
    if (token) config.headers.Authorization = `Bearer ${token}`
    if (APP_ENV === 'development') {
      console.debug(`[API] ${config.method?.toUpperCase()} ${config.baseURL}${config.url}`)
    }
    return config
  })
}

// Shared response interceptor: 401 → force logout.
function attachAuthErrorHandling(client: AxiosInstance) {
  client.interceptors.response.use(
    (res) => res,
    (error: AxiosError) => {
      if (error.response?.status === 401) {
        localStorage.removeItem('token')
        localStorage.removeItem('user_type')
        useAuthStore.getState().logout()
        window.location.href = '/login'
      }
      if (APP_ENV === 'development') {
        console.error('[API Error]', error.response?.status, error.response?.data)
      }
      return Promise.reject(error)
    }
  )
}

attachAuth(restClient)
attachAuth(restClientV2)
attachAuthErrorHandling(restClient)
attachAuthErrorHandling(restClientV2)

export function getApiError(err: unknown): string {
  if (axios.isAxiosError(err)) {
    const d = err.response?.data as Record<string, unknown> | undefined

    // Laravel-style field validation: { message, errors: { field: [msg, ...] } }.
    // Surface the specific field messages — they're far more useful than the
    // generic top-level "Validation Error".
    const fieldMsgs = flattenFieldErrors(d?.errors)
    if (fieldMsgs.length) return fieldMsgs.join('\n')

    if (typeof d?.message === 'string') return d.message
    if (typeof d?.error === 'string') return d.error
    if (err.response?.status === 422) return 'Validation failed. Check your inputs.'
    if (err.response?.status === 403) return 'You do not have permission.'
    if (err.response?.status === 404) return 'Resource not found.'
    if (err.response?.status === 500) return 'Server error. Please try again.'
    if (err.message) return err.message
  }
  return 'Something went wrong. Please try again.'
}

// Turn { field: ["msg1", "msg2"], other: "msg" } into a flat list of messages.
function flattenFieldErrors(errors: unknown): string[] {
  if (!errors || typeof errors !== 'object') return []
  const out: string[] = []
  for (const v of Object.values(errors as Record<string, unknown>)) {
    if (Array.isArray(v)) v.forEach(m => typeof m === 'string' && out.push(m))
    else if (typeof v === 'string') out.push(v)
  }
  return out
}
